<template>
  <div :style="computedColors">
    <div class="min-h-screen">
      <RouterView />
    </div>
    <GlobalFooter class="print:hidden" />
    <ConsentBanner v-if="!doorsaleStore.isActive" class="print:hidden" />
    <GlobalToast class="print:hidden" />
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeMount, onMounted, onUnmounted, ref, watchEffect } from 'vue';
  import { RouterView } from 'vue-router';

  import i18n from '@/i18n';
  import router from '@/router';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useDoorsaleStore } from '@/stores/doorsaleStore';
  import { useGeneralStore } from '@/stores/general';
  import { useSettingsStore } from '@/stores/settings';
  import { useSsoStore } from '@/stores/sso';
  import { useWebsocketStore } from '@/websocket/websocket';

  import ConsentBanner from './components/globals/ConsentBanner.vue';
  import GlobalFooter from './components/globals/GlobalFooter.vue';
  import GlobalToast from './components/ui/GlobalToast.vue';
  import { initAnalytics, trackAllPageView } from './utils/analytics';
  import { closeEmbedDrawer } from './utils/embedNavigation';
  import { getTextColorForBackground } from './utils/getTextColorForBackground';
  import { isEmbed } from './utils/isEmbed';
  import { useEmbedCartSync } from './utils/useEmbedCartSync';
  import EventDetailView from './views/EventDetailView.vue';
  import EventListView from './views/EventListView.vue';

  const primaryColor = ref('#171717');
  const primaryDarkColor = ref('color-mix(in srgb, var(--color-primary) 90%, #000 10%)');

  const computedColors = computed(() => {
    if (!primaryColor.value) {
      return {};
    }

    const primaryTextColor = getTextColorForBackground(primaryColor.value);

    // NOTE calculated css properties are not recalculated when their dependencies change, so you must update these also.
    return {
      '--color-primary': primaryColor.value,
      '--color-primary-text':
        primaryTextColor === 'light'
          ? 'var(--color-surface-light)'
          : 'var(--color-surface-dark)',
      '--color-primary-dark': primaryDarkColor.value,
    };
  });

  watchEffect(() => {
    Object.entries(computedColors.value).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  });

  ///////// Routing adjustments //////////
  // Route adjustments inserted here to ensure it runs early in the app lifecycle, also this is where the RouterView component is located. We can't put it in src/router/index.ts since the store isnt initialized yet when it is executed.

  const generalStore = useGeneralStore();
  const settingsStore = useSettingsStore();
  const ssoStore = useSsoStore();

  useEmbedCartSync();

  settingsStore.fetchSettings();
  generalStore.fetchGeneral().then(() => {
    // Needs the customer from the general data. The websocket waits for it before subscribing to an event.
    ssoStore.init();

    let reloadNeeded = false;

    // Event short_urls
    if (generalStore.general?.short_urls) {
      Object.entries(generalStore.short_urls()).forEach(([shortUrl, eventId]) => {
        // Check if the shortUrl already exists in the router
        const existingShortUrlRoute = router.getRoutes().find(route => route.path === `/${shortUrl}`);
        if (existingShortUrlRoute) {
          console.warn(`Route for shortUrl '${shortUrl}' already exists. Skipping.`); // TODO DELETE
          return;
        }

        router.addRoute({
          path: `/${shortUrl}`,
          name: `${shortUrl}`,
          alias: `/${shortUrl}`,
          component: EventDetailView, // Use the default component if available src/views/EventDetailView.vue
          props: { shortURL: `${shortUrl}` },
        });
      });
      reloadNeeded = true;
    }

    // Category short_urls
    if (generalStore.general?.categories) {
      generalStore.general.categories.forEach((category) => {
        if (category.short_url) {
          // Check if the shortUrl already exists in the router
          const existingShortUrlRoute = router.getRoutes().find(route => route.path === `/${category.short_url}`);
          if (existingShortUrlRoute) {
            return;
          }

          router.addRoute({
            path: `/${category.short_url}`,
            name: `alias-category-${category.short_url}`,
            alias: `/${category.short_url}`,
            component: EventListView, // Use the default component if available
            props: { categoryShortURL: `${category.short_url}` },
          });
        }
      });
      reloadNeeded = true;
    }

    // Special category short_urls
    if (generalStore.general?.special_categories) {
      generalStore.general.special_categories.forEach((sc) => {
        if (sc.short_url) {
          const existingShortUrlRoute = router.getRoutes().find(route => route.path === `/${sc.short_url}`);
          if (existingShortUrlRoute) {
            return;
          }

          router.addRoute({
            path: `/${sc.short_url}`,
            name: `alias-special-category-${sc.short_url}`,
            alias: `/${sc.short_url}`,
            component: EventListView,
            props: { specialCategoryShortURL: `${sc.short_url}` },
          });
        }
      });
      reloadNeeded = true;
    }

    //Never reload during checkout or receipt page, it causes bugs
    if(router.currentRoute.value.name?.toString().startsWith('checkout') ){
      reloadNeeded = false;
    }

    // Apply customer primary_language only when the user has not explicitly chosen a locale
    if (!localStorage.getItem('selectedLocale')) {
      const customerPrimaryLang = generalStore.customer().primary_language;
      if (customerPrimaryLang === 'da' || customerPrimaryLang === 'en') {
        i18n.global.locale.value = customerPrimaryLang;
      }
    }

    // Set primary colour from customer settings
    const customColour = generalStore.customer().custom_colour;
    if (customColour) {
      primaryColor.value = customColour;
    }

    // Inject customer custom CSS
    const customCss = generalStore.customer().custom_css;
    if (customCss) {
      const style = document.createElement('style');
      style.setAttribute('data-custom-css', '');
      style.textContent = customCss;
      document.head.appendChild(style);
    }

    // Initialize analytics (GA4, GTM, FB Pixel) — only outside embeds
    if (!isEmbed()) {
      initAnalytics(generalStore.customer());

      // Track page views on route changes
      router.afterEach((to) => {
        trackAllPageView(to.fullPath, document.title);
      });
    }

    // Reprocess the current path after adding aliases
    // Use redirectedFrom so a numeric short URL like /1234 (redirected to /event/1234
    // by the legacy route before its dynamic route was registered) re-navigates to the
    // original path where the newly added exact route now takes priority.
    if (reloadNeeded) {
      const currentPath = router.currentRoute.value.redirectedFrom?.fullPath ?? router.currentRoute.value.fullPath;
      router.replace('/').then(() => {
        router.replace(currentPath);
      });
    }
  });

  // Restore the WS connection on app start for non-doorsale routes. Doorsale routes
  // are excluded because their connection must be opened by the router guard (after
  // the admin session is confirmed and the clientID is cleared), so the fresh clientID
  // fetch happens while the sessionid cookie is active. window.location.pathname is
  // used instead of router.currentRoute because the route may not be resolved yet
  // at this point in <script setup>.
  const websocket = useWebsocketStore();
  if (!window.location.pathname.startsWith('/doorsale')) {
    websocket.restoreConnection();
  }
  //If we are not in checkout, remove empty event message
  let cleanUpEmptyEvents = false;
  if(!router.currentRoute.value.name?.toString().startsWith('checkout') && !router.currentRoute.value.name?.toString().startsWith('receipt') ){
    websocket.send('cleanupeventsbasket', {  }, undefined);
    cleanUpEmptyEvents = true;
    console.log('Sent cleanupeventsbasket message to websocket on app start');
  }

  //Trigger cleanup on route change, if we are not in checkout and something is in cart
  router.beforeEach((to, from, next) => {
    const cartStore = useCartStore();
    if(!to.name?.toString().startsWith('checkout') && !to.name?.toString().startsWith('receipt') && cartStore.items.length > 0){
      websocket.send('cleanupeventsbasket', {  }, undefined);
      console.log('Sent cleanupeventsbasket message to websocket on route change');
      const tempCurrentOrderData = cartStore.currentOrderData;
      console.log('Saved current order data before cleanup:', tempCurrentOrderData);
      //In case we move from checkout-information or checkout-tickets, we need to save the current order data
      cartStore.loadCartData(cleanUpEmptyEvents);
      if(from.name == 'checkout-information' || from.name == 'checkout-tickets') {
        cartStore.currentOrderData = tempCurrentOrderData;
      }
    }
    next();
  });

  ///////// Inactivity timeout //////////
  const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
  let inactivityTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  let siteEntryTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  let inactivityRouteUnsubscribe: (() => void) | undefined = undefined;
  const SESSION_EXPIRY_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min
  let sessionExpiryCheckInterval: ReturnType<typeof setInterval> | undefined = undefined;
  const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'click', 'scroll', 'touchstart', 'touchmove'] as const;
  const doorsaleStore = useDoorsaleStore();

  function clearInactivityTimers() {
    if (inactivityTimer !== undefined) { clearTimeout(inactivityTimer); inactivityTimer = undefined; }
    if (siteEntryTimer !== undefined) { clearTimeout(siteEntryTimer); siteEntryTimer = undefined; }
  }

  function handleInactivity() {
    const currentRoute = router.currentRoute.value;
    const currentRouteName = currentRoute.name?.toString() ?? '';

    // Exempt terminal receipts and ticket activation from session cleanup and redirects.
    if (isInactivityExemptRoute(currentRouteName)) return;

    const cartStore = useCartStore();
    const checkoutStore = useCheckoutStore();

    // Rule 5: capture active event ID BEFORE clearing checkout data
    const activeEventId = checkoutStore.id;

    // Reset stores
    console.log('User inactive for 20 minutes – resetting session and cart data.');
    cartStore.resetCart();

    // Set bypassPaymentGuard BEFORE router.push so the onBeforeRouteLeave guard
    // in CheckoutCancelPaymentModal lets navigation through without showing the modal.
    checkoutStore.bypassPaymentGuard = true;
    checkoutStore.showCancelPaymentModal = false;
    checkoutStore.clearCheckoutData();

    // Clear all cached data from storage, preserving consent preferences
    const consentPrefs = localStorage.getItem('SFT_consent_preferences');
    const cookieExpire = localStorage.getItem('SFT_cookie_warning_expire');
    localStorage.clear();
    if (consentPrefs) localStorage.setItem('SFT_consent_preferences', consentPrefs);
    if (cookieExpire) localStorage.setItem('SFT_cookie_warning_expire', cookieExpire);
    sessionStorage.clear();

    // Wipe the in-memory rtoken so the next connection gets a fresh server session.
    // localStorage.clear() above handles storage, but rtoken.value lives in Pinia memory
    // and now survives websocket.close() — stateChanged() no longer clears it (that wipe
    // was removed to fix the doorsale reconnect bug). Without this line, the next user
    // would reconnect with the old rtoken and receive the previous user's basket.
    websocket.clearSession();

    // Also clear the in-memory clientID. localStorage.clear() wiped it from storage,
    // but clientID.value in Pinia memory is not touched by clearSession(). Without this,
    // the reconnect after inactivity reuses the stale clientID which may have
    // has_doorsale_permission = 0, preventing doorsale ticket reservations.
    websocket.clearClientID();

    // Close websocket connection
    websocket.close();

    console.log('User inactive for 20 minutes – session cleared.');
    console.log('Current route:', currentRoute.fullPath);
    console.log('route name:', currentRouteName);

    // Rules 1 & 2: do not navigate if already on frontpage or event detail page
    const isOnFrontpage = currentRouteName === 'event-list' || currentRoute.path === '/';
    const isOnEventDetail = currentRouteName === 'event-detail' || currentRouteName.startsWith('alias-');
    if (isOnFrontpage || isOnEventDetail) return;

    // Doorsale session: always return to the doorsale event list
    if (doorsaleStore.isActive) {
      router.push({ name: 'doorsale-events' });
      return;
    }

    if (closeEmbedDrawer()) return;

    // Rule 5: navigate to the active event's detail page if one was in progress
    if (activeEventId) {
      router.push({ name: 'event-detail', params: { id: activeEventId } });
    } else {
      // Default: navigate to frontpage
      router.push('/');
    }
  }

  function resetInactivityTimer() {
    const currentRouteName = router.currentRoute.value.name?.toString() ?? '';
    if (isInactivityExemptRoute(currentRouteName)) {
      clearInactivityTimers();
      return;
    }
    if (inactivityTimer !== undefined) clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(handleInactivity, INACTIVITY_TIMEOUT_MS);
    doorsaleStore.sessionKeepalive();
  }

  function isInactivityExemptRoute(routeName: string) {
    return routeName === 'checkout-receipt' || routeName === 'selfscan';
  }

  onMounted(() => {
    document.querySelector('#of2-initial-event-links')?.remove();
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, resetInactivityTimer, { passive: true });
    });
    if (!isInactivityExemptRoute(router.currentRoute.value.name?.toString() ?? '')) {
      resetInactivityTimer();
      // Hard session timer: fires once 20 minutes after entering the site, regardless of activity.
      siteEntryTimer = setTimeout(handleInactivity, INACTIVITY_TIMEOUT_MS);
    }

    inactivityRouteUnsubscribe = router.afterEach((to, from) => {
      const routeName = to.name?.toString() ?? '';
      if (isInactivityExemptRoute(routeName)) {
        clearInactivityTimers();
      } else if (from.name?.toString() === 'selfscan') {
        resetInactivityTimer();
        siteEntryTimer = setTimeout(handleInactivity, INACTIVITY_TIMEOUT_MS);
      }
    });

    // Proactive doorsale session expiry check — runs every 60s regardless of activity.
    // Route check ensures the redirect only fires when the user is on a doorsale page;
    // isActive alone is not enough because sessionId persists if the user navigated away.
    // checkAdminSession() performs a read-only server check (no TTL refresh) so idle
    // users are not kept logged in. sessionKeepalive() is intentionally NOT called here;
    // only user activity (resetInactivityTimer) should extend the session.
    sessionExpiryCheckInterval = setInterval(async () => {
      const currentRouteName = router.currentRoute.value.name?.toString() ?? '';
      console.log('[doorsale] expiry tick — isActive:', doorsaleStore.isActive, '| route:', currentRouteName);
      if (doorsaleStore.isActive && currentRouteName.startsWith('doorsale-')) {
        await doorsaleStore.checkAdminSession();
        if (doorsaleStore.isSessionExpired()) {
          doorsaleStore.handleSessionExpired();
        }
      }
    }, SESSION_EXPIRY_CHECK_INTERVAL_MS);
  });

  onUnmounted(() => {
    ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, resetInactivityTimer);
    });
    clearInactivityTimers();
    inactivityRouteUnsubscribe?.();
    if (sessionExpiryCheckInterval !== undefined) {
      clearInterval(sessionExpiryCheckInterval);
      sessionExpiryCheckInterval = undefined;
    }
  });

  onBeforeMount(() => {
    const cartStore = useCartStore();
    if(cartStore.firstBasketLoad){
      console.log('Loading cart data on app start');
      cartStore.loadCartData(cleanUpEmptyEvents);
      cartStore.firstBasketLoad = false;
    }

  });
</script>
